import { useEffect, useRef, useCallback } from 'react'
import { AuthStateContext } from '../providers/AuthStateContext'
import { Notification } from '../models/Notification.model'

export interface UseWebSocketOptions {
  url: string
  onMessage: (notification: Notification) => void
  onError?: (error: Event) => void
  onOpen?: () => void
  onClose?: () => void
  enabled?: boolean
}

export const useWebSocket = ({
  url,
  onMessage,
  onError,
  onOpen,
  onClose,
  enabled = true,
}: UseWebSocketOptions) => {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const isManualCloseRef = useRef(false)
  const isConnectingRef = useRef(false)
  const enabledRef = useRef(enabled)
  const maxReconnectAttempts = 5
  const reconnectDelay = 3000 // 3 seconds

  // Store callbacks in refs to avoid recreating connections when they change
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  const onOpenRef = useRef(onOpen)
  const onCloseRef = useRef(onClose)

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage
    onErrorRef.current = onError
    onOpenRef.current = onOpen
    onCloseRef.current = onClose
  }, [onMessage, onError, onOpen, onClose])

  // Update enabled ref when it changes
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!enabledRef.current) {
      return
    }

    // Prevent duplicate connections
    if (isConnectingRef.current) {
      return
    }

    // Clear any pending reconnection attempts before creating a new connection
    clearReconnectTimeout()

    // Check if a connection already exists and is in a valid state
    if (wsRef.current) {
      const state = wsRef.current.readyState
      // If connecting (0) or open (1), don't create a new connection
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        return
      }
      // If closing (2) or closed (3), clean up the old connection first
      if (state === WebSocket.CLOSING || state === WebSocket.CLOSED) {
        wsRef.current = null
      }
    }

    const token = AuthStateContext.getToken()
    if (!token) {
      // Silently skip connection if no token (user might not be fully authenticated yet)
      return
    }

    try {
      isConnectingRef.current = true
      const wsUrl = `${url}?token=${encodeURIComponent(token)}`
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        // Only process if this is still the current connection
        if (wsRef.current !== ws) {
          return
        }
        isConnectingRef.current = false
        reconnectAttemptsRef.current = 0
        isManualCloseRef.current = false
        console.log('WebSocket connected successfully')
        onOpenRef.current?.()
      }

      ws.onmessage = (event) => {
        try {
          const notification: Notification = JSON.parse(event.data)
          onMessageRef.current(notification)
        } catch (error) {
          // Silently handle parsing errors
        }
      }

      ws.onerror = (error) => {
        // Only process if this is still the current connection
        if (wsRef.current !== ws) {
          return
        }
        isConnectingRef.current = false
        // Silently handle connection errors - server might not be running
        onErrorRef.current?.(error)
      }

      ws.onclose = (event) => {
        isConnectingRef.current = false

        // Only process close event if this is still the current connection
        // This prevents issues with React Strict Mode double mounting
        if (wsRef.current !== ws) {
          return
        }

        onCloseRef.current?.()

        // Only reconnect if it was an unexpected disconnect (not manual close)
        // and we haven't exceeded max attempts
        if (
          !isManualCloseRef.current &&
          reconnectAttemptsRef.current < maxReconnectAttempts &&
          enabledRef.current
        ) {
          reconnectAttemptsRef.current += 1
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, reconnectDelay * reconnectAttemptsRef.current)
        } else {
          // Reset attempts if manually closed or max attempts reached
          reconnectAttemptsRef.current = 0
        }

        // Clear the reference after close
        wsRef.current = null
      }

      wsRef.current = ws
    } catch (error) {
      isConnectingRef.current = false
      // Silently handle connection creation errors
    }
  }, [url, clearReconnectTimeout])

  const disconnect = useCallback(() => {
    // Clear any pending reconnection attempts
    clearReconnectTimeout()

    // Mark as manual close to prevent reconnection
    isManualCloseRef.current = true
    isConnectingRef.current = false

    // Close the WebSocket connection if it exists
    if (wsRef.current) {
      const ws = wsRef.current
      const currentState = ws.readyState

      // Remove event handlers first to prevent reconnection logic from firing
      ws.onclose = null
      ws.onerror = null
      ws.onopen = null
      ws.onmessage = null

      // Only close if the connection is actually open
      // If still connecting, just clear the ref - the connection will fail naturally
      if (currentState === WebSocket.OPEN) {
        try {
          ws.close()
        } catch (error) {
          // Silently handle errors when closing
        }
      }
      // If CONNECTING, don't call close() - just let it fail naturally
      // This prevents "closed before connection established" errors in React Strict Mode

      wsRef.current = null
    }

    // Reset reconnection attempts
    reconnectAttemptsRef.current = 0
  }, [clearReconnectTimeout])

  // Only depend on enabled and url - use refs for callbacks to prevent re-renders
  useEffect(() => {
    if (enabled) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      // Cleanup on unmount or when dependencies change
      clearReconnectTimeout()
      
      if (wsRef.current) {
        const ws = wsRef.current
        const currentState = ws.readyState
        
        // Mark as manual close to prevent reconnection
        isManualCloseRef.current = true
        isConnectingRef.current = false
        
        // Remove event handlers to prevent callbacks
        // Set handlers to no-ops instead of null to prevent errors
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const noop = () => {}
        ws.onclose = noop
        ws.onerror = noop
        ws.onopen = noop
        ws.onmessage = noop
        
        // Only close if already open - don't close if still connecting
        // This prevents "closed before connection established" errors in React Strict Mode
        if (currentState === WebSocket.OPEN) {
          try {
            ws.close()
          } catch (error) {
            // Silently handle errors
          }
        }
        // If CONNECTING, don't call close() - just clear the ref
        // The connection will fail naturally and the error handler won't fire
        // because we've already cleared the ref
        
        wsRef.current = null
        reconnectAttemptsRef.current = 0
      }
    }
  }, [enabled, url, connect, disconnect, clearReconnectTimeout])

  return {
    connect,
    disconnect,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  }
}
